import React, { useState } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { ShieldCheck, Building2, FileEdit, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * v2.54 — Per-user permissions toggles (Phase C Part 3).
 * Admin opens this from /admin?tab=users → "Permissions" button per row.
 * Toggles individual flags; backend defaults all-ON, this is OPT-OUT.
 */
const ROWS = [
  { key: "edit_listing", label: "Edit own listings", icon: FileEdit, hint: "Dealer/Builder edit kar sake apni properties" },
  { key: "delete_listing", label: "Delete own listings", icon: Trash2, hint: "Apni properties delete kar sake" },
  { key: "edit_project", label: "Edit own projects", icon: Building2, hint: "Builder/Owner apne projects edit kar sake" },
  { key: "delete_project", label: "Delete own projects", icon: Trash2, hint: "Apne projects delete kar sake" },
];

export default function AdminPermissionsModal({ user, open, onClose, onSaved }) {
  const startPerms = user?.permissions || { edit_listing: true, delete_listing: true, edit_project: true, delete_project: true };
  const [perms, setPerms] = useState(startPerms);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open && user) {
      setPerms(user.permissions || { edit_listing: true, delete_listing: true, edit_project: true, delete_project: true });
    }
  }, [open, user]);

  if (!user) return null;
  const isAdmin = user.role === "admin";

  const toggle = (k) => setPerms((p) => ({ ...p, [k]: !p[k] }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/users/${user.id}/permissions`, perms);
      toast.success("Permissions updated");
      if (onSaved) onSaved(r.data?.user);
      onClose();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Save failed"));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="admin-permissions-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#0055FF]" />
            Inventory Permissions
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-1 mb-4">
          <div className="text-[13px] font-semibold text-[#09090B]">{user.name || user.email}</div>
          <div className="text-[11.5px] text-[#52525B]">{user.email} · {user.role}</div>
        </div>
        {isAdmin ? (
          <div className="bg-[#FEE2E2] border border-[#FECACA] text-[#991B1B] text-[12.5px] p-3 rounded-sm">
            Admin permissions cannot be restricted.
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {ROWS.map((row) => {
                const Icon = row.icon;
                const enabled = !!perms[row.key];
                return (
                  <div key={row.key} className="flex items-start justify-between gap-3 p-3 border border-[#E4E4E7] rounded-sm" data-testid={`perm-row-${row.key}`}>
                    <div className="flex items-start gap-2 flex-1">
                      <Icon size={14} className={`mt-0.5 ${enabled ? "text-[#0055FF]" : "text-[#A1A1AA]"}`} />
                      <div>
                        <div className="text-[12.5px] font-semibold text-[#09090B]">{row.label}</div>
                        <div className="text-[11px] text-[#52525B]">{row.hint}</div>
                      </div>
                    </div>
                    <Switch
                      checked={enabled}
                      onCheckedChange={() => toggle(row.key)}
                      data-testid={`perm-toggle-${row.key}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={onClose} className="rounded-sm" data-testid="perm-cancel">Cancel</Button>
              <Button onClick={save} disabled={busy} className="btn-brand rounded-sm" data-testid="perm-save">
                {busy ? "Saving…" : "Save"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
